import { CommonMultiDeleteModel, FilteredPagedModel, FilteredPagedQueryResult } from "../../entities/common";
import { MessageInfoModel, MessageModel, SendMessageToUserModel, SendMessageToUsersModel } from "../../entities/message";
import { getQuerySearchParams } from "../../utils";
import { identitySlice, TagType } from "./identityApi";

export const messagesApi = identitySlice.injectEndpoints({
  endpoints: (builder) => ({
    // Message

    getMessageCommonInfo: builder.query<MessageInfoModel, void>({
      query: () => "/messages/commonInfo",
      providesTags: [{ type: TagType.Message, id: "LIST" }],
    }),
    getMessagesFilteredPaged: builder.query<FilteredPagedQueryResult<MessageModel>, FilteredPagedModel>({
      query: ({ page = 1, pageSize = 10, searchTerm = "", sortFieldName = "", descending = false, ...rest }: FilteredPagedModel) => {
        let url = `/messages/pageable?page=${page}&pageSize=${pageSize}&searchTerm=${searchTerm}&sortFieldName=${sortFieldName}&descending=${descending}`;
        const restParams = getQuerySearchParams(rest);
        if (restParams) {
          url += `&${restParams}`;
        }

        return url;
      },
      providesTags: (result, error, arg) =>
        result
          ? [
              { type: TagType.Message, id: "PARTIAL-LIST" },
              ...result.data.map(({ id }) => ({
                type: TagType.Message,
                id,
              })),
            ]
          : [{ type: TagType.Message, id: "PARTIAL-LIST" }],
    }),
    getMessage: builder.query<MessageModel, number>({
      query: (messageId) => `/messages/${messageId}`,
      providesTags: (result, error, arg) => [{ type: TagType.Message, id: arg }],
    }),
    sendMessageToUser: builder.mutation({
      query: (model: SendMessageToUserModel) => ({
        url: "/messages/send",
        method: "POST",
        body: model,
      }),
      invalidatesTags: [
        { type: TagType.Message, id: "LIST" },
        { type: TagType.Message, id: "PARTIAL-LIST" },
      ],
    }),
    sendMessageToUsers: builder.mutation({
      query: (model: SendMessageToUsersModel) => ({
        url: "/messages/send/multi-user",
        method: "POST",
        body: model,
      }),
      invalidatesTags: [
        { type: TagType.Message, id: "LIST" },
        { type: TagType.Message, id: "PARTIAL-LIST" },
      ],
    }),
    markMessageAsRead: builder.mutation({
      query: (messageId: number) => ({
        url: `/messages/${messageId}/read`,
        method: "PUT",
      }),
      invalidatesTags: (result, error, arg) => (error ? [] : [{ type: TagType.Message, id: arg }]),
    }),
    deleteMessage: builder.mutation({
      query: (messageId: number) => ({
        url: `/messages/${messageId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              { type: TagType.Message, id: arg },
              { type: TagType.Message, id: "LIST" },
            ],
    }),
    deleteMultiMessages: builder.mutation({
      query: (multiDeleteModel: CommonMultiDeleteModel) => ({
        url: "/messages/multiDelete",
        method: "DELETE",
        body: multiDeleteModel,
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              ...arg.ids.map((id) => ({
                type: TagType.Message,
                id,
              })),
              { type: TagType.Message, id: "LIST" },
            ],
    }),
  }),
});

export const {
  useGetMessageCommonInfoQuery,
  useGetMessagesFilteredPagedQuery,
  useGetMessageQuery,
  useSendMessageToUserMutation,
  useSendMessageToUsersMutation,
  useMarkMessageAsReadMutation,
  useDeleteMessageMutation,
  useDeleteMultiMessagesMutation,
} = messagesApi;
